import { useTranslation } from 'react-i18next';
import { Plug, RefreshCw, Trash2 } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { formatDateYearMonthDay } from '@utils/date';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import type { Connection } from '@utils/types/connections/connection.ts';
import { needsReconnectForUpload } from '@utils/connections/upload-scope.ts';
import {
  canEnableVideoUploads,
  isPlatformAllowed
} from '@utils/connections/policy.ts';
import ReconnectForUploadNotice from '@components/publishing/publish-dialog/reconnect-for-upload-notice';
import PlatformIcon from './platform-icon';
import ConnectionStatusBadge from './connection-status-badge';
import YouTubeUploadToggle from './youtube-upload-toggle';

interface Props {
  connections: Connection[];
  isLoading: boolean;
  onConnect: () => void;
  onReconnect: (connection: Connection) => void;
  onDisconnect: (connection: Connection) => void;
}

export default function ConnectionsList({
  connections,
  isLoading,
  onConnect,
  onReconnect,
  onDisconnect
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (!isLoading && connections.length === 0) {
    return (
      <EmptyState
        icon={<Plug className="h-7 w-7" aria-hidden="true" />}
        title={t('publishing-no-connections-title')}
        subtitle={t('publishing-no-connections-subtitle')}
        action={
          <Button
            variant="primary"
            size="small"
            label={t('publishing-connect-a-platform')}
            onClick={onConnect}
          />
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">
            {t('publishing-connections-title')}
          </h3>
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('publishing-connections-subtitle')}
          </p>
        </div>
        <Button
          variant="primary"
          size="small"
          label={t('publishing-connect-a-platform')}
          onClick={onConnect}
        />
      </div>

      <ul
        className="flex flex-col rounded-xl border divide-y"
        style={{
          borderColor: getColour('hairline'),
          background: getColour('secondLayer')
        }}
        aria-busy={isLoading}
      >
        {connections.map(connection => {
          const spec = getPlatformSpec(connection.platform);
          const platformLabel = spec?.displayName ?? connection.platform;
          const platformBlocked = !isPlatformAllowed(connection);
          // A blocked platform cannot publish, so reconnecting it would only
          // lead to a refused connect. Disconnect stays available.
          const needsReconnect =
            connection.status === 'needs_reconnect' && !platformBlocked;
          const connectedBy = connection.connected_by?.name;
          const connectedAt = connection.connected_at
            ? formatDateYearMonthDay(connection.connected_at)
            : null;
          const lastUsed = connection.last_used_at
            ? formatDateYearMonthDay(connection.last_used_at)
            : t('publishing-never-used');
          // AC 3.5 — uploads are switched on but Google never granted the
          // upload scope on this grant, so every upload would be refused.
          // Say so on the row rather than letting the toggle look ready.
          const showUploadScopeAlert =
            !platformBlocked && needsReconnectForUpload(connection);
          // The operator decides whether uploads can be offered at all.
          const showUploadToggle =
            !platformBlocked &&
            connection.platform === 'youtube' &&
            connection.status === 'connected' &&
            canEnableVideoUploads(connection);

          return (
            <li
              key={connection.id}
              className="flex flex-col gap-3 p-4"
              style={{ borderColor: getColour('hairline') }}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                    style={{ background: getColour('thirdLayer') }}
                  >
                    <PlatformIcon
                      platform={connection.platform}
                      className="h-5 w-5"
                    />
                  </div>
                  <div className="min-w-0 flex flex-col gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate">
                        {platformLabel}
                      </span>
                      <ConnectionStatusBadge status={connection.status} />
                      {platformBlocked && (
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{
                            background: getColour('thirdLayer'),
                            color: getColour('inactiveText')
                          }}
                        >
                          {t('publishing-connection-platform-blocked')}
                        </span>
                      )}
                    </div>
                    <span
                      className="text-xs truncate"
                      style={{ color: getColour('inactiveText') }}
                      title={connection.account_name}
                    >
                      {connection.account_name}
                    </span>
                    <span
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {connectedBy && connectedAt
                        ? t('publishing-connected-by-at', {
                            name: connectedBy,
                            date: connectedAt
                          })
                        : connectedBy
                          ? t('publishing-connected-by', { name: connectedBy })
                          : connectedAt}
                    </span>
                    <span
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('publishing-last-used', { date: lastUsed })}
                    </span>
                    {connection.status_message && (
                      <span
                        className="text-xs"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {connection.status_message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 shrink-0">
                  {showUploadToggle && (
                    <YouTubeUploadToggle connection={connection} />
                  )}
                  {needsReconnect && (
                    <Button
                      variant="primary"
                      size="small"
                      label={t('publishing-reconnect')}
                      icon={
                        <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      }
                      onClick={() => onReconnect(connection)}
                    />
                  )}
                  <Button
                    variant="smoke"
                    size="small"
                    label={t('publishing-disconnect')}
                    icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => onDisconnect(connection)}
                  />
                </div>
              </div>

              {platformBlocked && (
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('publishing-connection-platform-blocked-help')}
                </p>
              )}

              {showUploadScopeAlert && (
                <ReconnectForUploadNotice
                  onReconnect={() => onReconnect(connection)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
